import utilService from "@/service/utilService";
import notice from "@/service/notice";
import cryptoWrapper from "@/service/cryptoWrapper";
import {mapState} from "vuex";

const SERVICE_URL=process.env.VUE_APP_SERVICE_URL;

/**
 * 방문예약 관련 Mixin
 *
 * - 승인, 반려 등 처리
 * - 알림발송 (주의: 비즈엠에 등록된 템플릿과 완전히 일치해야 함)
 *
 */
export default {
    data: () => ({
        statusInfos: {
            WAITING: {
                text: '인증 대기',
                color: 'black',
                backgroundColor: 'yellow',
            },
            PENDING: {
                text: '승인 대기',
                color: 'white',
                backgroundColor: 'red',
            },
            APPROVED: {
                text: '승인',
                color: 'white',
                backgroundColor: 'blue',
            },
            REJECTED: {
                text: '반려',
                color: 'black',
                backgroundColor: 'gray',
            },
            CANCELED: {
                text: '취소',
                color: 'white',
                backgroundColor: 'black',
            }
        },
    }),

    computed: {
        ...mapState(['currentUser', 'secretKey', 'iv']),
        senderPhone() {
            return this.$code.get('service.notice.send_phone_no');
        },
    },

    methods: {
        getStatusInfo(statusCode) {
            return this.statusInfos[statusCode];
        },

        getEncryptedVisitId(visitId) {
            return cryptoWrapper.encrypt(visitId, this.secretKey, this.iv);
        },

        async getApplyInfo(visitId) {
            const encryptedVisitId = this.getEncryptedVisitId(visitId);
            const path = this.$route.path;
            const params = {};


            let url;
            let res;

            if (path.startsWith('/backoffice')) { // 개인정보 마스킹 X
                url = '/v1/res/visit/visitInfo';
                params.visitId = encryptedVisitId;
                res = await this.$axios.post(url, params);
            } else if (path.startsWith('/kiosk')) { // 개인정보 마스킹 X
                url = '/v1/kiosk/visitInfo';
                params.visitId = visitId;
                res = await this.$axios.get(url, {
                    params
                });
            } else { // 개인정보 마스킹 O
                url = '/v1/home/visit/visitInfo';
                params.visitId = encryptedVisitId;
                res = await this.$axios.post(url, params);
            }

            return {
                visitInfo: res.data.visitInfo,
                visitors: res.data.visitors,
                carryInItems: res.data.importItems
            }
        },

        /**
         * 방문 승인
         */
        async approve(visitInfo, visitors) {
            await this.$axios.post(`/v1/home/visit/approve/${visitInfo.visitId}`);
            await this.notificationApproved(visitInfo, visitors);
        },

        /**
         * 방문 반려
         */
        async reject(visitInfo, visitors, reason) {
            await this.$axios.post(`/v1/home/visit/reject/${visitInfo.visitId}`, {
                voidReason: reason ? reason : '(사유 없음)'
            });
            await this.notificationRejected(visitInfo, visitors, reason);
        },

        /**
         * 방문 취소
         */
        async cancel(visitInfo, visitors, reason) {
            await this.$axios.post(`/v1/home/visit/cancel/${visitInfo.visitId}`, {
                voidReason: reason
            });
            await this.notificationCanceled(visitInfo, visitors, reason);
        },

        async notificationArrival(visitInfo) {
            const senderPhone = this.$code.get('service.notice.send_phone_no');
            const recipientPhone = visitInfo.visitUserMobile;
            const date = this.$moment(visitInfo.startDatetime).format('YYYY-MM-DD');
            const time = this.$moment(visitInfo.startDatetime).format('A hh:mm');
            /*
            [ 아이센스 방문신청 서비스 ]
방문객이 #{방문회사명} #{방문장소}에 도착했습니다.

▶ 신청인 : #{신청인}
▶ 신청인 회사명 : #{신청인 회사명}
▶ 신청인 휴대폰번호 : #{휴대폰번호}
▶ 방문일시 : #{방문날짜} #{방문시간}
▶ 방문목적 : #{방문목적}
             */
            const message = `[ 아이센스 방문신청 서비스 ]
방문객이 ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}에 도착했습니다.

▶ 신청인 : ${visitInfo.agentName}
▶ 신청인 회사명 : ${visitInfo.agentCompany}
▶ 신청인 휴대폰번호 : ${utilService.humanReadableMobile(visitInfo.agentMobile)}
▶ 방문일시 : ${date} ${time}
▶ 방문목적 : ${visitInfo.purpose}`;
            const encryptedVisitId = cryptoWrapper.encrypt(visitInfo.visitId, this.secretKey, this.iv);
            const buttonUrl = `${SERVICE_URL}/home/visitInfo/t/${encryptedVisitId}`;
            await notice.arrivalSendSms(senderPhone, recipientPhone, message, buttonUrl);
        },

        async notificationRejected(visitInfo, visitors, reason) {
            const startDatetime = this.$moment(visitInfo.startDatetime);
            const startDate = startDatetime.format('YYYY-MM-DD');
            const startTime = startDatetime.format('A hh:mm');

            // 방문자에게 반려 메시지 전송
            for (let index in visitors) {
                const visitor = visitors[index];
                const recipientPhone = visitor.visitorMobile;
                const message = `[ 아이센스 방문신청 서비스 ]
방문신청 반려처리 안내

▶ ${visitor.visitorName}님, ${startDate} ${startTime}
▶ 반려 사유: ${reason}

방문신청이 반려 되어 확인 후 재 신청 바랍니다.`;

                await notice.rejectSendSms(this.senderPhone, recipientPhone, message);
            }
        },

        async notificationApproved(visitInfo, visitors) {
            const encryptedVisitId = this.getEncryptedVisitId(visitInfo.visitId)
            const qrcodeUrl = `${SERVICE_URL}/home/qrcode/${encryptedVisitId}`;
            const startDatetime = this.$moment(visitInfo.startDatetime);
            const startDate = startDatetime.format('YYYY-MM-DD');
            const startTime = startDatetime.format('A hh:mm');
            const message = `[ 아이센스 방문신청 서비스 ]
방문신청 승인 완료

▶ 방문회사 : ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}
▶ 방문일시 : ${startDate} ${startTime}
▶ 승인 QR 코드`;

            for (let index in visitors) {
                const visitor = visitors[index];
                const recipientPhone = visitor.visitorMobile;
                await notice.confirmSendSms(this.senderPhone, recipientPhone, message, qrcodeUrl);
            }
        },

        async notificationApplication(visitInfo, visitors) {
            const encryptedVisitId = this.getEncryptedVisitId(visitInfo.visitId);
            const senderPhone = this.$code.get('service.notice.send_phone_no');
            const date = this.$moment(visitInfo.startDatetime).format('YYYY-MM-DD');
            const time = this.$moment(visitInfo.startDatetime).format('A hh:mm');

            const approveUrl = `${SERVICE_URL}/home/visitInfo/t/${encryptedVisitId}`;
            const recipientPhone = cryptoWrapper.decrypt(visitInfo.visitUserEncryptedMobile, this.secretKey, this.iv);

            if (!recipientPhone) {
                this.$window.alert('접견 대상자의 휴대폰 전화번호가 없습니다.', 'error');
                return;
            }

            if (!Array.isArray(visitors) && visitors.length === 0) {
                return;
            }

            let visitorInfo = visitors[0].visitorName;
            if (visitors.length > 1) {
                visitorInfo = `${visitorInfo} 외 ${visitors.length - 1}`;
            }

            const message = `[ 아이센스 방문신청 서비스 ]
방문신청 알림메세지

▶ 신청자 : ${visitInfo.agentName}
▶ 회사명 : ${visitInfo.agentCompany}
▶ 휴대폰번호 : ${utilService.humanReadableMobile(visitInfo.agentMobile)}
▶ 방문자 : ${visitorInfo}

▶ 방문회사 : ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}
▶ 방문일시 : ${date} ${time}
▶ 방문목적 : ${visitInfo.purpose}

방문 승인 완료처리 부탁드립니다.`;

            await notice.applySendSms(senderPhone, recipientPhone, message, approveUrl);
        },

        async notificationCanceled(visitInfo, visitors, reason) {
            const senderPhone = this.$code.get('service.notice.send_phone_no');
            const date = this.$moment(visitInfo.startDatetime).format('YYYY-MM-DD');
            const time = this.$moment(visitInfo.startDatetime).format('A hh:mm');
            const recipientPhoneList = visitors.map(e => e.visitorMobile);

            if (visitInfo.visitUserMobile) { // 접견자 휴대폰
                recipientPhoneList.push(visitInfo.visitUserMobile);
            }

            const message = `[ 아이센스 방문신청 서비스 ]
방문예약이 취소되었습니다.

▶ 신청인 : ${visitInfo.agentName}
▶ 회사명 : ${visitInfo.agentCompany}
▶ 휴대폰번호 : ${utilService.humanReadableMobile(visitInfo.agentMobile)}
▶ 방문회사 : ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}
▶ 방문일시 : ${date} ${time}
▶ 방문목적 : ${visitInfo.purpose}
▶ 취소 사유 : ${reason}`;

            for (let i in recipientPhoneList) {
                const recipientPhone = recipientPhoneList[i];
                await notice.cancelSendSms(senderPhone, recipientPhone, message);
            }
        },
    }

}